/**
 * AI Preview Dialog (MA-AI-001)
 * 
 * Shows a preview of AI-suggested changes before applying.
 * Allows user to confirm or cancel the AI edit.
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AIPreviewDialog({
  open,
  onOpenChange,
  originalContent,
  previewContent,
  explanation,
  changesSummary,
  elementType,
  onApply,
  onCancel,
  isApplying = false,
}) {
  const handleApply = () => {
    onApply?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  // Format content for display
  const formatContent = (content, type) => {
    if (!content) return null;
    
    if (type === "step") {
      return (
        <div className="space-y-3">
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Message</span>
            <p className="text-sm text-slate-200 mt-1">{content.message}</p>
          </div>
          {content.responses && content.responses.length > 0 && (
            <div>
              <span className="text-xs text-slate-500 uppercase tracking-wide">Response Options</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {content.responses.map((r, i) => (
                  <Badge 
                    key={i} 
                    variant="outline" 
                    className="bg-indigo-500/10 border-indigo-500/30 text-indigo-200"
                  >
                    {typeof r === 'string' ? r : r.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    if (type === "system_message") {
      return <p className="text-sm text-slate-200">{content.message}</p>;
    }
    
    if (type === "response_button") {
      return (
        <Badge 
          variant="outline" 
          className="bg-indigo-500/10 border-indigo-500/30 text-indigo-200"
        >
          {content.label}
        </Badge>
      );
    }
    
    // MA-BUG-FIX: Handle booking section types
    if (type === "booking_header" || type === "booking_today" || type === "booking_tomorrow") {
      return <p className="text-sm text-slate-200">{content.message}</p>;
    }
    
    // Fallback: try to extract message if it exists, otherwise stringify
    if (content.message) {
      return <p className="text-sm text-slate-200">{content.message}</p>;
    }
    
    return <p className="text-sm text-slate-200">{JSON.stringify(content)}</p>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-lg border-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <Sparkles className="h-5 w-5 text-indigo-400" />
            AI Suggestion Preview
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {explanation || "Review the AI-suggested changes before applying."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Original Content */}
          <div className="p-3 rounded-lg bg-slate-800/50 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Original</span>
            </div>
            {formatContent(originalContent, elementType)}
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <ArrowRight className="h-5 w-5 text-indigo-400" />
          </div>

          {/* Preview Content */}
          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3 w-3 text-indigo-400" />
              <span className="text-xs font-medium text-indigo-300 uppercase tracking-wide">AI Suggestion</span>
              {changesSummary && (
                <Badge className="ml-auto text-[10px] bg-indigo-500/20 text-indigo-300 border-0">
                  {changesSummary}
                </Badge>
              )}
            </div>
            {formatContent(previewContent, elementType)}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-white/10 text-slate-200 hover:bg-white/5"
            disabled={isApplying}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
            disabled={isApplying}
          >
            {isApplying ? (
              <>
                <span className="animate-spin mr-1">⏳</span>
                Applying...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Apply Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
